import { getRoot, types } from 'mobx-state-tree';

import Paths from '../../types/Paths';
import { ProductClassType } from '../../types/ProductClass';
import ProductTypeClass, { ProductType } from '../../types/ProductTypeClass';
import generatePath from '../../util/generatePath';
import FormField from '../FormField';
import CoreProduct from '../product/CoreProduct';

const OrderProduct = types.compose(
  CoreProduct,
  types.model('OrderProduct', {
    actual_code: types.maybeNull(types.string),
    canonical_path: types.optional(types.array(types.string), []),
    class: ProductClassType,
    has_discount: types.boolean,
    id: types.identifier,
    image_url: types.maybeNull(types.string),
    is_free_of_charge: types.boolean,
    main_category_id: types.maybeNull(types.number),
    main_section_id: types.maybeNull(types.number),
    manufacturers_name: types.maybeNull(types.string),
    multiproduct_title: types.maybeNull(types.string),
    name: types.maybeNull(types.string),
    package_info: types.string,
    product_id: types.maybeNull(types.string),
    product_url: types.maybeNull(types.string),
    quantity_delivered: types.maybeNull(types.number),
    quantity_ordered: types.maybeNull(types.number),
    recurring_order_custom_cycle: types.maybeNull(types.number),
    recurring_order_cycle_type: types.maybeNull(types.string),
    reference_field: types.maybeNull(FormField),
    section_ids: types.array(types.integer),
    slug: types.maybeNull(types.string),
    stock_unit: types.maybeNull(types.string),
    tax_rate: types.string,
    total_price_formatted: types.string,
    type: types.maybeNull(ProductType),
    unit_normal_price_formatted: types.string,
    unit_price_formatted: types.string,
    url: types.maybeNull(types.string),
    variations: types.optional(types.array(types.string), []),
    warranty_in_months: types.maybeNull(types.integer),
  }))
  .named("OrderProduct")
  .views((self) => {
    const withTax = getRoot(self).accountStore.showPricesWithTax;

    return {
      getPrice() {
        if (self.hasPrice) {
          return self.price_info.getPrice(withTax);
        }

        return null;
      },
      get canonicalPath() {
        return self.canonical_path;
      },
      get canonicalCategoryId() {
        return self.main_category_id;
      },
      get finalUnitPrice() {
        return self.final_unit_price;
      },
      get hasPrice() {
        return !!self.price_info;
      },
      get isOnSale() {
        return self.priceInfo?.is_discount;
      },
      get isPhysicalProduct() {
        return self.type === ProductTypeClass.PHYSICAL;
      },
      get manufacturer() {
        return self.manufacturers_name;
      },
      get normalPrice() {
        return self.starting_unit_price;
      },
      get normalUnitPrice() {
        return self.starting_unit_price;
      },
      get priceInfo() {
        return self.price_info;
      },
      get productCode() {
        return self.actual_code;
      },
      get productId() {
        return self.product_id;
      },
      get reference() {
        return self.reference_field?.value || null;
      },
      get savings() {
        return Number(
          (self.starting_unit_price - self.final_unit_price).toFixed(2)
        );
      },
      get path() {
        if (!self.slug || !self.product_id) {
          return '';
        }

        const slug = self.slug;

        const path = generatePath(Paths.Product, {
          slug,
          id: self.product_id,
        });

        return path;
      },
      get totalQuantity() {
        return self.package_size && self.package_size !== 0
          ? self.quantity * self.package_size
          : self.quantity;
      },
      get unitName() {
        const unit = self.sellInPackage
          ? self.package_unit_name
          : self.stock_unit;
        return unit ?? self.stock_unit;
      },
    };
  }).actions((self) => ({
    updateReferenceField(newValue) {
      self.reference_field.value = newValue;
    }
  }));

export default OrderProduct;
